import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Close } from 'assets/icons';
import Button from 'components/button';
import {
  fillText,
  monthLabel,
} from 'components/invoiceImport/invoiceImportText';
import { formatEuros } from 'components/invoiceList/invoiceAmount';
import InvoiceMailReport from 'components/invoiceSendMail/InvoiceMailReport';
import {
  INVOICE_MAIL_SENT,
  buildMailReport,
  contactName,
  contactQualifier,
  countText,
  createRequestId,
  invoiceSendMailText,
  requestFailureText,
  statusText,
} from 'components/invoiceSendMail/invoiceSendMailText';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import useModal from 'components/modal/useModal';
import { companyRoleLabel } from 'constants/companyRoles';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

const RADIO_CLASS_NAME = 'radio-primary';
const CHECKBOX_CLASS_NAME = 'checkbox-primary';

const contactKey = (companyId, contact) =>
  `${companyId}|${contact.ref.kind}:${contact.ref.id}`;

const sendableInvoicesOf = (company) =>
  (company.invoices ?? []).filter((invoice) => invoice.sendable);

// No answer, a server failure, or the api still running this very request id:
// the first send may have gone (or will go) through with the body it carried.
const isOutcomeUnknown = (error) =>
  !error?.response ||
  error.response.status >= 500 ||
  error.response.data?.errorCode === 'SEND_IN_PROGRESS';

/**
 * « Envoyer par mail » (JBWEB-1282, mockups 6.1 to 6.3). `modalData` is
 * `{ invoiceNumbers }`, the selection of the list or the invoice of the
 * detail. The api works out the scopes, the companies and their billing
 * contacts (`prepare`), read again for each scope chosen; the send names the
 * contacts ticked by reference, company by company.
 *
 * The request id is drawn at the opening and kept until the modal closes: a
 * retry after a lost answer is the same send to the api, which answers with
 * what it already did. A new opening is a new send.
 *
 * The api replays a known request id without reading the new body, so once a
 * send's outcome is unknown the body it carried is frozen: only that exact
 * body may leave again, or the admin would believe a new selection was sent.
 */
const ModalInvoiceSendMail = () => {
  const { toggle } = useModal();
  const { language, modalData, setModalDataReturn } = UseContext();
  const text = invoiceSendMailText(language);

  // Frozen at the opening: the list behind may change its selection meanwhile.
  const [invoiceNumbers] = useState(() =>
    Array.isArray(modalData?.invoiceNumbers) ? modalData.invoiceNumbers : [],
  );
  const [requestId] = useState(createRequestId);
  const [scope, setScope] = useState('selection');
  const [loadKey, setLoadKey] = useState(0);
  const [prepared, setPrepared] = useState(null);
  const [scopes, setScopes] = useState(null);
  const [selectionInvoice, setSelectionInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailure, setLoadFailure] = useState(null);
  // The admin's ticks, by company and contact; any other contact keeps the
  // api's `preselected`, from one scope to the next.
  const [ticks, setTicks] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [sendFailure, setSendFailure] = useState(null);
  // `{ body, companyNames }` of a send whose outcome is unknown; never cleared
  // while the modal is open, since a later refusal does not prove the first
  // attempt is over.
  const [frozenSend, setFrozenSend] = useState(null);
  const isFrozen = frozenSend !== null;
  // Two clicks in the same instant must not start two sends.
  const sendingRef = useRef(false);
  const textRef = useRef(text);
  textRef.current = text;

  useEffect(() => {
    if (invoiceNumbers.length === 0) return undefined;
    let active = true;
    setIsLoading(true);
    setLoadFailure(null);
    ApiDatabase.postInvoiceMailPrepare(
      { invoiceNumbers, scope },
      (response) => {
        if (!active) return;
        const data = response?.data ?? null;
        setPrepared(data);
        if (data?.scopes) setScopes(data.scopes);
        if (scope === 'selection' && invoiceNumbers.length === 1) {
          const company = data?.companies?.find((candidate) =>
            candidate.invoices?.some(
              (invoice) => invoice.number === invoiceNumbers[0],
            ),
          );
          const invoice = company?.invoices.find(
            (candidate) => candidate.number === invoiceNumbers[0],
          );
          if (invoice) setSelectionInvoice({ ...invoice, company });
        }
        setIsLoading(false);
      },
      (error) => {
        if (!active || error?.name === 'CanceledError') return;
        setPrepared(null);
        setLoadFailure(
          requestFailureText(textRef.current, error, {
            fallbackKey: 'prepareFailed',
          }),
        );
        setIsLoading(false);
      },
    );
    return () => {
      active = false;
    };
  }, [invoiceNumbers, scope, loadKey]);

  if (invoiceNumbers.length === 0) return null;

  const companies = (!isLoading && prepared?.companies) || [];
  const isTicked = (company, contact) => {
    const key = contactKey(company.id, contact);
    return key in ticks ? ticks[key] : contact.preselected === true;
  };
  const tickedContactsOf = (company) =>
    company.id
      ? company.contacts.filter((contact) => isTicked(company, contact))
      : [];
  const sendCount = companies
    .filter((company) => tickedContactsOf(company).length > 0)
    .reduce((total, company) => total + sendableInvoicesOf(company).length, 0);
  const canSend = isFrozen
    ? !isSending
    : sendCount > 0 && !isSending && !isLoading && !loadFailure;
  const isChoiceLocked = isSending || isFrozen;

  const companyNameOf = (company) => company?.name || text('withoutCompany');

  const chooseScope = (next) => {
    if (isChoiceLocked || next === scope) return;
    setSendFailure(null);
    setScope(next);
  };

  const toggleContact = (company, contact) => {
    if (isChoiceLocked) return;
    setSendFailure(null);
    const key = contactKey(company.id, contact);
    const ticked = isTicked(company, contact);
    setTicks((current) => ({ ...current, [key]: !ticked }));
  };

  const buildAttempt = () => ({
    body: {
      requestId,
      scope,
      invoiceNumbers,
      companies: companies
        .filter((company) => sendableInvoicesOf(company).length > 0)
        .map((company) => ({
          companyId: company.id,
          // References only: the api finds the addresses itself.
          recipients: tickedContactsOf(company).map(({ ref }) => ({
            kind: ref.kind,
            id: ref.id,
          })),
        }))
        .filter((company) => company.recipients.length > 0),
    },
    companyNames: new Map(
      companies.map((company) => [company.id, company.name]),
    ),
  });

  const send = () => {
    if (!canSend || sendingRef.current) return;
    sendingRef.current = true;
    const attempt = frozenSend ?? buildAttempt();
    const { body, companyNames } = attempt;
    setIsSending(true);
    setSendFailure(null);
    ApiDatabase.postInvoiceMail(
      body,
      (response) => {
        sendingRef.current = false;
        const data = response?.data ?? {};
        const report = buildMailReport({
          text: textRef.current,
          results: data.results,
          replayed: data.replayed === true,
          companyNames,
        });
        toast[report.kind](<InvoiceMailReport lines={report.lines} />);
        setModalDataReturn({
          type: INVOICE_MAIL_SENT,
          invoiceNumbers: report.sentNumbers,
        });
        toggle();
      },
      (error) => {
        sendingRef.current = false;
        setIsSending(false);
        if (isOutcomeUnknown(error)) setFrozenSend(attempt);
        // Never the error object itself in the console: axios keeps the
        // request, the admin's token included.
        setSendFailure(
          requestFailureText(textRef.current, error, {
            fallbackKey: 'generic',
            networkKey: 'network',
          }),
        );
      },
    );
  };

  const period = prepared?.period ? monthLabel(prepared.period) : '';
  const scopeOptions = [];
  if (invoiceNumbers.length === 1) {
    scopeOptions.push({
      id: 'selection',
      label: text('scope.selectionOne'),
      detail: selectionInvoice
        ? fillText(text('scope.selectionOneDetail'), {
            number: selectionInvoice.number,
            company: companyNameOf(selectionInvoice.company),
            amount:
              selectionInvoice.amountInclTax == null
                ? '—'
                : formatEuros(selectionInvoice.amountInclTax),
          })
        : '',
    });
  } else {
    scopeOptions.push({
      id: 'selection',
      label: text('scope.selectionMany'),
      detail: fillText(text('scope.selectionManyDetail'), {
        count: invoiceNumbers.length,
      }),
    });
  }
  if (scopes?.company) {
    scopeOptions.push({
      id: 'company',
      label: fillText(text('scope.company'), {
        company: companyNameOf(scopes.company),
      }),
      detail: [countText(text, 'scope.invoices', scopes.company.count), period]
        .filter(Boolean)
        .join(' · '),
    });
  }
  if (scopes?.group) {
    scopeOptions.push({
      id: 'group',
      label: fillText(text('scope.group'), { group: scopes.group.name ?? '' }),
      detail: [
        countText(text, 'scope.invoices', scopes.group.count),
        countText(text, 'scope.companies', scopes.group.companyCount),
        period,
      ]
        .filter(Boolean)
        .join(' · '),
    });
  }

  const renderContact = (company, contact) => {
    const roleLabel =
      contact.label === 'member' && contact.role != null
        ? companyRoleLabel(language, contact.role)
        : '';
    return (
      <label
        key={contactKey(company.id, contact)}
        className="flex items-start gap-3 px-4 py-3 cursor-pointer"
      >
        <input
          type="checkbox"
          className={CHECKBOX_CLASS_NAME}
          checked={isTicked(company, contact)}
          disabled={isChoiceLocked}
          onChange={() => toggleContact(company, contact)}
        />
        <span className="flex flex-col">
          <span className="font-medium text-gray-900">
            {contactName(text, contact)}
          </span>
          <span className="text-gray-500">
            {[contact.email, contactQualifier(text, contact, roleLabel)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
      </label>
    );
  };

  const renderCompany = (company) => {
    const notSendable = (company.invoices ?? []).filter(
      (invoice) => !invoice.sendable,
    );
    const contacts = company.id ? (company.contacts ?? []) : [];
    return (
      <section
        key={company.id ?? 'without-company'}
        aria-label={fillText(text('recipients.title'), {
          company: companyNameOf(company),
        })}
        className="flex flex-col gap-2"
      >
        <div className="font-medium text-gray-700">
          {fillText(text('recipients.title'), {
            company: companyNameOf(company),
          })}
        </div>
        {contacts.length > 0 ? (
          <div className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-100">
            {contacts.map((contact) => renderContact(company, contact))}
          </div>
        ) : (
          <div className="rounded-lg border border-gray-100 px-4 py-3 text-gray-500">
            {text('recipients.none')}
          </div>
        )}
        {notSendable.length > 0 && (
          <div className="text-xs text-gray-500">
            <span>{text('notSendable.title')} </span>
            {notSendable
              .map((invoice) =>
                fillText(text('notSendable.item'), {
                  number: invoice.number,
                  status: statusText(text, invoice.status),
                }),
              )
              .join(', ')}
          </div>
        )}
      </section>
    );
  };

  return (
    <>
      <div className="flex flex-shrink-0 items-start justify-between gap-3 px-6 pt-5 pb-3 rounded-t">
        <h3 className="ft-lg tab:text-xl text-[#202124] font-semibold my-auto">
          {text('title')}
        </h3>
        <Button
          size="TABLE"
          color="TABLE"
          aria-label={text('close')}
          title={text('close')}
          disabled={isSending}
          className="disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={toggle}
        >
          <Close wh={20} color="#374151" />
        </Button>
      </div>
      <ModalBody>
        <div className="flex flex-col gap-5">
          <div className="text-gray-500">{text('intro')}</div>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 font-medium text-gray-700">
              {text('scopesLabel')}
            </legend>
            <div className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-100">
              {scopeOptions.map((option) => (
                <label
                  key={option.id}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer ${
                    scope === option.id ? 'bg-gray-50' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="invoice-mail-scope"
                    className={RADIO_CLASS_NAME}
                    checked={scope === option.id}
                    disabled={isChoiceLocked}
                    onChange={() => chooseScope(option.id)}
                  />
                  <span className="flex flex-col">
                    <span className="font-medium text-gray-900">
                      {option.label}
                    </span>
                    {option.detail && (
                      <span className="text-gray-500">{option.detail}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          {isLoading && (
            <div role="status" className="py-6 text-center text-gray-500">
              {text('loading')}
            </div>
          )}
          {!isLoading && loadFailure && (
            <div
              role="alert"
              className="flex items-center justify-between gap-3 rounded-lg bg-red-50 px-4 py-3 text-red-700"
            >
              <span>{loadFailure}</span>
              <Button
                color="SECONDARY"
                onClick={() => setLoadKey((key) => key + 1)}
              >
                {text('reload')}
              </Button>
            </div>
          )}
          {companies.map(renderCompany)}
          {(sendFailure || isFrozen) && (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-lg bg-red-50 px-4 py-3 text-red-700"
            >
              {sendFailure && <p>{sendFailure}</p>}
              {isFrozen && <p>{text('frozenNotice')}</p>}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          size="LG"
          color={isSending ? 'DISABLED' : 'SECONDARY'}
          disabled={isSending}
          onClick={toggle}
        >
          {text('cancel')}
        </Button>
        <Button
          size="LG"
          color={canSend ? 'PRIMARY' : 'DISABLED'}
          disabled={!canSend}
          onClick={send}
        >
          {isSending
            ? text('sending')
            : isFrozen
              ? text('retrySend')
              : countText(text, 'send', sendCount)}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalInvoiceSendMail;
